import React from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Card,
  CardContent,
  Divider, 
  Chip, 
  useTheme, 
  Stack,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Theme 
} from '@mui/material';
import { 
  TrendingUp, 
  TrendingDown, 
  TrendingFlat,
  ExpandMore,
  ArrowUpward,
  ArrowDownward,
  HorizontalRule,
  InfoOutlined,
  CheckCircleOutline,
  WarningAmber,
  DangerousOutlined,
  Insights,
  BarChart,
  PieChart,
  ShowChart,
  Psychology,
  Recommend,
  Assignment,
  CalendarToday,
  DateRange,
  MonetizationOn, 
  Warning,
  SentimentVerySatisfied, // For Strong Gain/Inflow
  SentimentSatisfied, // For Gain/Inflow
  SentimentNeutral, // For Flat
  SentimentDissatisfied, // For Loss/Outflow
  SentimentVeryDissatisfied, // For Strong Loss/Outflow
  Security, // For Low Risk
  ReportProblem, // For High Risk
  Lightbulb as RecommendationsIcon,
  Category as SectorIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import ReactMarkdown from 'react-markdown';
import { MarketReportFull, MarketReportDetails, InvestmentRecommendation, TrendReport, WorkCommentaryItem } from '../../types/investment'; 

// --- Styled Components ---

const ReportPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(4),
  pageBreakInside: 'avoid', // Attempt to prevent page breaks inside sections when printing
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  color: theme.palette.primary.main,
}));

const SubSectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(1),
}));

const MarkdownContent = styled(Box)(({ theme }) => ({
  '& p': {
    marginBottom: theme.spacing(1.5),
    lineHeight: 1.6,
  },
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    marginTop: theme.spacing(2.5),
    marginBottom: theme.spacing(1),
    fontWeight: 'bold',
  },
  '& ul, & ol': {
    marginLeft: theme.spacing(2.5),
    marginBottom: theme.spacing(1.5),
  },
  '& li': {
    marginBottom: theme.spacing(0.8),
  },
  '& blockquote': {
    borderLeft: `4px solid ${theme.palette.grey[400]}`,
    paddingLeft: theme.spacing(2),
    margin: theme.spacing(1.5, 0),
    fontStyle: 'italic',
    color: theme.palette.text.secondary,
  },
  '& a': {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    }
  }
}));

const AllocationBarContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '20px',
  backgroundColor: theme.palette.grey[300],
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  position: 'relative',
  marginTop: theme.spacing(1),
}));

const AllocationBarSegment = styled(Box)<{ width: number; color: string }>(({ width, color }) => ({
  height: '100%',
  width: `${width}%`,
  backgroundColor: color,
  position: 'absolute',
  left: 0,
  top: 0,
}));

const AllocationMarker = styled(Box)<{ markerPosition: number; color: string }>(({ markerPosition, color, theme }) => ({
  position: 'absolute',
  left: `${markerPosition}%`, // Use markerPosition here
  top: '-4px',
  bottom: '-4px',
  width: '3px',
  backgroundColor: color,
  borderRadius: '1px',
  zIndex: 1,
  '&::after': { // Small circle indicator
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: color,
    border: `1px solid ${theme.palette.background.paper}`,
  }
}));

// --- Helper Functions ---

const formatPercent = (value: number | undefined | null): string => {
  if (value === undefined || value === null || isNaN(value)) return 'N/A';
  return `${value.toFixed(1)}%`;
};

const renderMarkdown = (content: string | undefined | null) => {
  if (!content) return <Typography variant="body2" color="text.secondary">N/A</Typography>; // Return Typography directly
  return (
    <MarkdownContent>
      <ReactMarkdown>{content}</ReactMarkdown>
    </MarkdownContent>
  );
};

const getTrendIcon = (trend: string | undefined | null) => {
  switch (trend?.toUpperCase()) {
    case 'IMPROVING': return <TrendingUp color="success" />;
    case 'DETERIORATING': return <TrendingDown color="error" />;
    case 'STEADY': return <TrendingFlat color="action" />;
    default: return <HorizontalRule color="disabled" />;
  }
};

const getProjectionIcon = (projection: string | undefined | null) => {
  switch (projection?.toUpperCase()) {
    case 'UP': return <ArrowUpward color="success" />;
    case 'DOWN': return <ArrowDownward color="error" />;
    case 'SIDEWAYS': return <HorizontalRule color="action" />;
    case 'IMPROVING': return <TrendingUp color="success" />; // Added missing cases
    case 'DETERIORATING': return <TrendingDown color="error" />; // Added missing cases
    case 'STEADY': return <TrendingFlat color="action" />; // Added missing cases
    default: return <HorizontalRule color="disabled" />;
  }
};

const getRecommendationIcon = (type: string | undefined | null) => {
  switch (type?.toLowerCase()) {
    case 'invest': return <CheckCircleOutline color="success" />; // Added missing cases
    case 'consider': return <WarningAmber color="warning" />; // Added missing cases
    case 'reduce': return <DangerousOutlined color="error" />; // Added missing cases
    default: return <InfoOutlined color="disabled" />;
  }
};

// --- New Trend Rating Renderers ---

// Accept theme as argument, remove useTheme()
export const renderRiskRating = (rating: string | undefined | null, theme: Theme) => { 
  const normalizedRating = rating?.toUpperCase() || 'N/A';
  let color: 'success' | 'warning' | 'error' | 'default' = 'default';
  let icon = <InfoOutlined fontSize="small" />;

  switch (normalizedRating) {
    case 'LOW':
      color = 'success';
      icon = <Security fontSize="small" />;
      break;
    case 'MEDIUM': // Assuming MEDIUM exists or might exist
      color = 'warning';
      icon = <WarningAmber fontSize="small" />;
      break;
    case 'HIGH':
      color = 'error';
      icon = <ReportProblem fontSize="small" />;
      break;
  }

  return (
    <Chip
      icon={icon}
      label={rating || 'N/A'}
      color={color}
      size="small"
      variant="outlined"
      sx={{ fontWeight: 'bold' }}
    />
  );
};

// Accept theme as argument, remove useTheme()
export const renderPerformanceRating = (rating: string | undefined | null, theme: Theme) => { 
  const normalizedRating = rating?.toUpperCase() || 'N/A';
  let color: 'success' | 'warning' | 'error' | 'info' | 'default' = 'default';
  let icon = <InfoOutlined fontSize="small" />;

  switch (normalizedRating) {
    case 'STRONG GAIN':
      color = 'success';
      icon = <SentimentVerySatisfied fontSize="small" />;
      break;
    case 'GAIN':
      color = 'success';
      icon = <SentimentSatisfied fontSize="small" />;
      break;
    case 'FLAT':
      color = 'info';
      icon = <SentimentNeutral fontSize="small" />;
      break;
    case 'LOSS':
      color = 'error';
      icon = <SentimentDissatisfied fontSize="small" />;
      break;
    case 'STRONG LOSS':
      color = 'error';
      icon = <SentimentVeryDissatisfied fontSize="small" />;
      break;
  }

  return (
    <Chip
      icon={icon}
      label={rating || 'N/A'}
      color={color}
      size="small"
      variant={color === 'info' || color === 'default' ? 'outlined' : 'filled'}
      sx={{ fontWeight: 'bold' }}
    />
  );
};

// Accept theme as argument, remove useTheme()
export const renderMoneyFlowRating = (rating: string | undefined | null, theme: Theme) => { 
  const normalizedRating = rating?.toUpperCase() || 'N/A';
  let color: 'success' | 'warning' | 'error' | 'info' | 'default' = 'default';
  let icon = <InfoOutlined fontSize="small" />;

  switch (normalizedRating) {
    case 'STRONG INFLOW':
      color = 'success';
      icon = <SentimentVerySatisfied fontSize="small" />;
      break;
    case 'INFLOW':
      color = 'success';
      icon = <SentimentSatisfied fontSize="small" />;
      break;
    case 'FLAT':
      color = 'info';
      icon = <SentimentNeutral fontSize="small" />;
      break;
    case 'OUTFLOW':
      color = 'error';
      icon = <SentimentDissatisfied fontSize="small" />;
      break;
    case 'STRONG OUTFLOW':
      color = 'error';
      icon = <SentimentVeryDissatisfied fontSize="small" />;
      break;
  }

  return (
    <Chip
      icon={icon}
      label={rating || 'N/A'}
      color={color}
      size="small"
      variant={color === 'info' || color === 'default' ? 'outlined' : 'filled'}
      sx={{ fontWeight: 'bold' }}
    />
  );
};


// --- Sub-Components ---

interface ReportSectionProps {
  title: string;
  reportDetails: MarketReportDetails | undefined;
  renderMarketRating: (rating: string, theme: Theme) => React.ReactElement; // Add theme param
}

const ReportSection: React.FC<ReportSectionProps> = ({ title, reportDetails, renderMarketRating }) => {
  const theme = useTheme();
  if (!reportDetails) return null;

  const {
    Classification,
    Rating,
    Trend,
    ProjectedTrend_ShortTerm,
    ProjectedTrend_MidTerm,
    ProjectedTrend_LongTerm,
    RatingTrendExplanation,
    Allocations
  } = reportDetails;

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>{Classification || title}</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', mb: 2, mx: -1 }}>
          {/* Rating Section */}
          <Box sx={{ width: { xs: '100%', sm: '50%', md: '25%' }, px: 1, mb: { xs: 2, sm: 1 } }}> {/* Added bottom margin */}
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}> {/* Added bottom margin to label */}
              Rating
            </Typography>
            {/* Rating element on its own line */}
            <Box>
              {renderMarketRating(Rating || 'N/A', theme)}
            </Box>
          </Box>

          {/* Trend Section */}
          <Box sx={{ width: { xs: '100%', sm: '50%', md: '25%' }, px: 1, mb: { xs: 2, sm: 1 } }}> {/* Added bottom margin */}
             <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}> {/* Added bottom margin to label */}
               Current Trend
             </Typography>
             <Stack direction="row" spacing={1} alignItems="center">
               {getTrendIcon(Trend)}
               <Typography variant="body2">{Trend || 'N/A'}</Typography>
             </Stack>
           </Box>

           {/* Projections Section */}
           <Box sx={{ width: { xs: '100%', md: '50%' }, px: 1, mb: { xs: 2, md: 1 } }}> {/* Added bottom margin */}
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}> {/* Added bottom margin to label */}
                Projected Trend
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                 <Tooltip title="Short Term (1 Month)">
                   <Stack direction="row" spacing={0.5} alignItems="center">
                     <Typography variant="caption" sx={{ fontWeight: 'bold', mr: 0.25 }}>ST:</Typography> {/* Added Label */}
                     {getProjectionIcon(ProjectedTrend_ShortTerm)}
                     <Typography variant="body2">{ProjectedTrend_ShortTerm || 'N/A'}</Typography>
                   </Stack>
                 </Tooltip>
                 <Tooltip title="Mid Term (3-6 Months)">
                   <Stack direction="row" spacing={0.5} alignItems="center">
                     <Typography variant="caption" sx={{ fontWeight: 'bold', mr: 0.25 }}>MT:</Typography> {/* Added Label */}
                     {getProjectionIcon(ProjectedTrend_MidTerm)}
                     <Typography variant="body2">{ProjectedTrend_MidTerm || 'N/A'}</Typography>
                   </Stack>
                 </Tooltip>
                 <Tooltip title="Long Term (1 Year)">
                   <Stack direction="row" spacing={0.5} alignItems="center">
                     <Typography variant="caption" sx={{ fontWeight: 'bold', mr: 0.25 }}>LT:</Typography> {/* Added Label */}
                     {getProjectionIcon(ProjectedTrend_LongTerm)}
                     <Typography variant="body2">{ProjectedTrend_LongTerm || 'N/A'}</Typography>
                   </Stack>
                 </Tooltip>
              </Stack>
           </Box>
        </Box>

        <SubSectionTitle variant="subtitle2">Explanation</SubSectionTitle>
        {renderMarkdown(RatingTrendExplanation)}

        {Allocations && (
          <>
            <SubSectionTitle variant="subtitle2">Recommended Allocation</SubSectionTitle>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', mx: -0.5 }}>
              <Box sx={{ width: { xs: '100%', sm: '66.67%' }, px: 0.5 }}>
                <Tooltip title={`Min: ${formatPercent(Allocations.min)} | Ideal: ${formatPercent(Allocations.ideal)} | Max: ${formatPercent(Allocations.max)}`}>
                  <AllocationBarContainer>
                    <AllocationBarSegment
                      width={Allocations.max}
                      color={theme.palette.success.light} // Max range
                    />
                     <AllocationBarSegment
                      width={Allocations.min}
                      color={theme.palette.grey[300]} // Below min range (covered by max)
                    />
                    <AllocationMarker markerPosition={Allocations.min} color={theme.palette.warning.main} />
                    <AllocationMarker markerPosition={Allocations.ideal} color={theme.palette.success.dark} />
                    <AllocationMarker markerPosition={Allocations.max} color={theme.palette.error.main} />
                  </AllocationBarContainer>
                </Tooltip>
              </Box>
              <Box sx={{ width: { xs: '100%', sm: '33.33%' }, px: 0.5, textAlign: { xs: 'left', sm: 'right' }, mt: { xs: 1, sm: 0 } }}>
                <Typography variant="h6" component="span" fontWeight="bold">{formatPercent(Allocations.ideal)}</Typography>
                <Typography variant="caption" color="text.secondary" component="span" sx={{ ml: 0.5 }}> (Ideal)</Typography>
              </Box>
            </Box>
            <Stack direction="row" spacing={2} mt={1}>
                 <Typography variant="caption">Min: {formatPercent(Allocations.min)}</Typography>
                 <Typography variant="caption">Max: {formatPercent(Allocations.max)}</Typography>
            </Stack>
          </>
        )}
      </CardContent>
    </Card>
  );
};

interface RecommendationCardProps {
  recommendation: InvestmentRecommendation;
}

const RecommendationCard: React.FC<RecommendationCardProps> = ({ recommendation }) => {
  const { RecommendationType, Details } = recommendation;
  if (!Details) return null;

  return (
    <Card variant="outlined" sx={{ mb: 2, pageBreakInside: 'avoid' }}>
      <CardContent>
        <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
          {getRecommendationIcon(RecommendationType)}
          <Typography variant="h6">{Details.Title || 'Recommendation'}</Typography>
          <Chip label={RecommendationType || 'Info'} size="small" sx={{ ml: 'auto' }} />
        </Stack>
        <SubSectionTitle variant="subtitle2">Reason</SubSectionTitle>
        {renderMarkdown(Details.Reason)}
        <SubSectionTitle variant="subtitle2">Risks</SubSectionTitle>
        {renderMarkdown(Details.Risks)}
        {Details.ComplementaryInvestment && (
          <>
            <SubSectionTitle variant="subtitle2">Complementary Investment</SubSectionTitle>
            {renderMarkdown(Details.ComplementaryInvestment)}
          </>
        )}
      </CardContent>
    </Card>
  );
};


// --- New Trend Report Section Component ---

interface TrendReportSectionProps {
  title: string;
  trendReport: TrendReport | undefined;
  renderSpecificRating: (rating: string, theme: Theme) => React.ReactElement; // Add theme param
  icon: React.ReactElement;
}

const TrendReportSection: React.FC<TrendReportSectionProps> = ({
  title,
  trendReport,
  renderSpecificRating, 
  icon
}) => {
  if (!trendReport) return null;

  const reportDate = trendReport.ReportDate ? new Date(trendReport.ReportDate + "T00:00:00") : null;
  const dataDates = trendReport.DataDates || 'N/A';

  return (
    <ReportPaper>
      <SectionTitle variant="h5">
        <Box component="span" sx={{ mr: 1, display: 'inline-flex', alignItems: 'center' }}>
          {icon}
        </Box>
        {title} Trend Analysis
      </SectionTitle>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems="center"
        spacing={1}
        divider={<Divider orientation="vertical" flexItem />}
        sx={{ mb: 2, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}
      >
         <Stack direction="row" alignItems="center" spacing={1}>
           <CalendarToday fontSize="small" color="action"/>
           <Typography variant="body2">Report Date: {reportDate ? reportDate.toLocaleDateString() : 'N/A'}</Typography>
         </Stack>
         <Stack direction="row" alignItems="center" spacing={1}>
           <DateRange fontSize="small" color="action"/>
           <Typography variant="body2">Data Range: {dataDates}</Typography>
         </Stack>
      </Stack>
      <Divider sx={{ my: 2 }}/>

      {/* Market All Up for Trend Report */}
      {trendReport.MarketAllUp?.Report && (
        <Box mb={3}>
          <SubSectionTitle variant="h6">Overall Market ({title})</SubSectionTitle>
          <ReportSection
            title={`Overall Market (${title})`}
            reportDetails={trendReport.MarketAllUp.Report}
            renderMarketRating={renderSpecificRating} // Pass the specific renderer
          />
        </Box>
      )}

      {/* Sectors for Trend Report */}
      {trendReport.Sectors?.Reports && trendReport.Sectors.Reports.length > 0 && (
        <Box mb={3}>
          <SubSectionTitle variant="h6">Sector Analysis ({title})</SubSectionTitle>
          {trendReport.Sectors.Reports.map((sectorReport, index) => (
            <ReportSection
              key={sectorReport.Classification || index}
              title={`Sector: ${sectorReport.Classification} (${title})`}
              reportDetails={sectorReport}
              renderMarketRating={renderSpecificRating} // Pass the specific renderer
            />
          ))}
        </Box>
      )}

      {/* Market Caps for Trend Report */}
      {trendReport.MarketCaps?.Reports && trendReport.MarketCaps.Reports.length > 0 && (
        <Box mb={3}>
          <SubSectionTitle variant="h6">Market Cap Analysis ({title})</SubSectionTitle>
          {trendReport.MarketCaps.Reports.map((marketCapReport, index) => (
            <ReportSection
              key={marketCapReport.Classification || index}
              title={`Market Cap: ${marketCapReport.Classification} (${title})`}
              reportDetails={marketCapReport}
              renderMarketRating={renderSpecificRating} // Pass the specific renderer
            />
          ))}
        </Box>
      )}

      {/* Recommendations for Trend Report */}
      {trendReport.Recommendations && (
        <Box>
          <SubSectionTitle variant="h6">{title} Recommendations</SubSectionTitle>
          {renderMarkdown(trendReport.Recommendations)}
        </Box>
      )}
    </ReportPaper>
  );
};


// --- Main Report Component ---

interface MarketInsightsReportProps {
  marketReportFull: MarketReportFull | null | undefined;
  renderMarketRating: (rating: string, theme: Theme) => React.ReactElement; // Add theme param
  renderMarketRegime: (regime: string) => React.ReactElement;
  onTabChange: (index: number, subtab?: number) => void;
}

const MarketInsightsReport: React.FC<MarketInsightsReportProps> = ({
  marketReportFull,
  renderMarketRating, 
  renderMarketRegime,
  onTabChange
}) => {
  const theme = useTheme(); // Get theme here to pass to main ReportSections

  if (!marketReportFull || !marketReportFull.market_report) {
    return (
      <Alert severity="warning" sx={{ m: 3 }}>
        Market Insights Report data is currently unavailable. Please try again later.
      </Alert>
    );
  }

  const report = marketReportFull.market_report;
  const trendReports = marketReportFull.trend_reports; 
  const reportDate = report.ReportDate ? new Date(report.ReportDate + "T00:00:00") : new Date();
  const dataDates = report.DataDates || 'N/A';

  const trendReportIcons: { [key: string]: React.ReactElement } = {
    "Money Flow": <MonetizationOn />,
    "Performance": <ShowChart />,
    "Risk": <Warning />,
  };

  // Update type for the map
  const trendRatingRenderers: { [key: string]: (rating: string, theme: Theme) => React.ReactElement } = { // Add theme param
    "Money Flow": renderMoneyFlowRating,
    "Performance": renderPerformanceRating,
    "Risk": renderRiskRating,
  };

  return (
    <Box 
      sx={{ 
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        px: 2, // Add horizontal padding on small screens
      }}
    >
      <Box 
        sx={{ 
          maxWidth: '1100px', // Maximum width for readability
          width: '100%',
          mx: 'auto', // Center the content horizontally
          mb: 6, // Add bottom margin
        }}
      >
        {/* Report Header */}
        <ReportPaper elevation={2}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.dark' }}>
            Market Insights Report
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems="center"
            spacing={1}
            divider={<Divider orientation="vertical" flexItem />}
            sx={{ mb: 2, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}
          >
             <Stack direction="row" alignItems="center" spacing={1}>
               <CalendarToday fontSize="small" color="action"/>
               <Typography variant="body2">Report Date: {reportDate.toLocaleDateString()}</Typography>
             </Stack>
             <Stack direction="row" alignItems="center" spacing={1}>
               <DateRange fontSize="small" color="action"/>
               <Typography variant="body2">Data Range: {dataDates}</Typography>
             </Stack>
          </Stack>
          <Divider sx={{ my: 2 }}/>

           {/* Market Status Summary */}
           <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-around', alignItems: 'center', textAlign: 'center', mb: 2, mx: -1 }}>
              <Box sx={{ width: { xs: '100%', sm: '33.33%' }, px: 1, mb: { xs: 2, sm: 1 } }}> {/* Added bottom margin */}
                  <Typography variant="overline" color="text.secondary" display="block" sx={{ mb: 0.5 }}> {/* Added bottom margin to label */}
                    Overall Rating
                  </Typography>
                  {/* Rating element on its own line */}
                  <Box>
                    {renderMarketRating(report.MarketRating || 'N/A', theme)} 
                  </Box>
              </Box>
              <Box sx={{ width: { xs: '100%', sm: '33.33%' }, px: 1, mb: { xs: 2, sm: 1 } }}> {/* Added bottom margin */}
                   <Typography variant="overline" color="text.secondary" display="block" sx={{ mb: 0.5 }}> {/* Added bottom margin to label */}
                     Current Trend
                   </Typography>
                   <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                       {getTrendIcon(report.MarketTrend)}
                       <Typography variant="body1">{report.MarketTrend || 'N/A'}</Typography>
                   </Stack>
               </Box>
               <Box sx={{ width: { xs: '100%', sm: '33.33%' }, px: 1, mb: { xs: 2, sm: 1 } }}> {/* Added bottom margin */}
                   <Typography variant="overline" color="text.secondary" display="block" sx={{ mb: 0.5 }}> {/* Added bottom margin to label */}
                     Market Regime
                   </Typography>
                   {/* Regime element on its own line */}
                   <Box>
                     {renderMarketRegime(report.MarketRegime || 'N/A')}
                   </Box>
               </Box>
           </Box>
        </ReportPaper>

        {/* Market Overview */}
        <ReportPaper>
          <SectionTitle variant="h5"><Insights sx={{ mr: 1 }}/>Market Overview</SectionTitle>
          {renderMarkdown(report.MarketOverview)}
        </ReportPaper>

        {/* Market-Wide Analysis */}
        {report.MarketAllUp?.Report && (
          <ReportPaper>
            <SectionTitle variant="h5"><BarChart sx={{ mr: 1 }}/>Market-Wide Analysis</SectionTitle>
            <ReportSection
              title="Overall Market"
              reportDetails={report.MarketAllUp.Report}
              renderMarketRating={renderMarketRating} // Pass the main renderer
            />
          </ReportPaper>
        )}

        {/* Sector Analysis */}
        {report.Sectors?.Reports && report.Sectors.Reports.length > 0 && (
          <ReportPaper>
            <SectionTitle variant="h5"><SectorIcon sx={{ mr: 1 }}/>Sector Analysis</SectionTitle>
            {report.Sectors.Reports.map((sectorReport, index) => (
              <ReportSection
                key={sectorReport.Classification || index}
                title={`Sector: ${sectorReport.Classification}`}
                reportDetails={sectorReport}
                renderMarketRating={renderMarketRating} // Pass the main renderer
              />
            ))}
          </ReportPaper>
        )}

        {/* Market Cap Analysis */}
        {report.MarketCaps?.Reports && report.MarketCaps.Reports.length > 0 && (
          <ReportPaper>
            <SectionTitle variant="h5"><ShowChart sx={{ mr: 1 }}/>Market Cap Analysis</SectionTitle>
            {report.MarketCaps.Reports.map((marketCapReport, index) => (
              <ReportSection
                key={marketCapReport.Classification || index}
                title={`Market Cap: ${marketCapReport.Classification}`}
                reportDetails={marketCapReport}
                renderMarketRating={renderMarketRating} // Pass the main renderer
              />
            ))}
          </ReportPaper>
        )}

        {/* Investment Recommendations */}
        {report.InvestmentRecommendations && report.InvestmentRecommendations.length > 0 && (
          <ReportPaper>
            <SectionTitle variant="h5">
              {/* <RecommendationsIcon sx={{ mr: 1 }}/> */}
              Investment Recommendations
            </SectionTitle>
            {report.InvestmentRecommendations.map((rec, index) => (
              <RecommendationCard key={index} recommendation={rec} />
            ))}
          </ReportPaper>
        )}


        {/* --- Trend Reports Section --- */}
        {trendReports && (
          <>
            {Object.entries(trendReports).map(([title, trendData]) => {
              // Get the specific renderer (already expects theme param)
              const specificRenderer = trendRatingRenderers[title] || renderMarketRating; 
              return (
                <TrendReportSection
                  key={title}
                  title={title}
                  trendReport={trendData}
                  renderSpecificRating={specificRenderer} 
                  icon={trendReportIcons[title] || <Insights />} 
                />
              );
            })}
          </>
        )}

        {/* Investment Philosophy */}
        {report.InvestmentPhilosophy && (
          <ReportPaper>
            <SectionTitle variant="h5"><Psychology sx={{ mr: 1 }}/>Investment Philosophy Alignment</SectionTitle>
            {renderMarkdown(report.InvestmentPhilosophy)}
          </ReportPaper>
        )}

        {/* Work Commentary (Appendix) */}
        {report.WorkCommentary && report.WorkCommentary.length > 0 && (
          <ReportPaper>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <SectionTitle variant="h6" sx={{ mb: 0 }}><Assignment sx={{ mr: 1 }}/>Report Generation Appendix</SectionTitle>
              </AccordionSummary>
              <AccordionDetails>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Activity</TableCell>
                        <TableCell>Outcome</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {report.WorkCommentary.map((item: WorkCommentaryItem, index: number) => (
                        <TableRow key={index}>
                          <TableCell>{item.Activity}</TableCell>
                          <TableCell>{item.Outcome}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          </ReportPaper>
        )}
      </Box>
    </Box>
  );
};

export default MarketInsightsReport;